import { Combobox } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { useState, Fragment, useMemo } from 'react'

interface Provider {
  id: string
  name: string
  businessName?: string
  fantasyName?: string
  phone?: string
  email?: string
}

interface ProviderComboboxProps {
  providers: Provider[]
  selectedProvider: Provider | null
  onSelect: (provider: Provider | null) => void
  onCreateProvider?: (query: string) => void
  placeholder?: string
  disabled?: boolean
}

const getProviderLabel = (p: Provider) => {
  // El nombre de fantasía manda (es como se lo conoce); la razón social entre paréntesis
  if (p.fantasyName && p.fantasyName !== p.name) {
    return `${p.fantasyName} (${p.name})`
  }
  if (p.businessName && p.businessName !== p.name) {
    return `${p.name} (${p.businessName})`
  }
  return p.name
}

export default function ProviderCombobox({
  providers,
  selectedProvider,
  onSelect,
  onCreateProvider,
  placeholder = 'Buscar proveedor...',
  disabled = false
}: ProviderComboboxProps) {
  const [query, setQuery] = useState('')

  const filteredProviders = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return providers
    return providers.filter((p) => {
      const name = (p.name || '').toLowerCase()
      const biz = (p.businessName || '').toLowerCase()
      const fantasy = (p.fantasyName || '').toLowerCase()
      const phone = (p.phone || '').toLowerCase()
      const email = (p.email || '').toLowerCase()
      return name.includes(q) || biz.includes(q) || fantasy.includes(q) || phone.includes(q) || email.includes(q)
    })
  }, [providers, query])

  const trimmedQuery = query.trim()
  const showCreateOption = !!onCreateProvider && !disabled && trimmedQuery.length > 0

  const handleChange = (provider: Provider | null) => {
    if (provider?.id === '__create__') {
      onCreateProvider?.(query.trim())
      return
    }
    onSelect(provider)
  }

  return (
    <Combobox value={selectedProvider} onChange={handleChange} disabled={disabled} by="id">
      <div className="relative">
        <div className="relative">
          <Combobox.Input
            className="w-full h-auto px-4 py-2 pl-10 pr-10 border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={placeholder}
            displayValue={(provider: Provider | null) => (provider ? getProviderLabel(provider) : '')}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <Combobox.Button className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            <ChevronUpDownIcon className="h-5 w-5" aria-hidden="true" />
          </Combobox.Button>
        </div>

        <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg shadow-lg border bg-card">
          {filteredProviders.length === 0 && !showCreateOption && trimmedQuery !== '' && (
            <div className="px-4 py-4 text-center text-muted-foreground">
              <p className="text-sm font-medium">No se encontraron proveedores</p>
              <p className="text-xs mt-0.5">Intenta buscar por nombre o razon social</p>
            </div>
          )}

          {filteredProviders.map((provider) => (
            <Combobox.Option key={provider.id} value={provider} as={Fragment}>
              {({ active, selected }) => (
                <li
                  className={`relative cursor-pointer select-none px-4 py-2 transition-colors ${
                    active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${selected ? 'font-semibold' : 'font-normal'}`}>
                      {getProviderLabel(provider)}
                    </span>
                    {selected && <CheckIcon className="h-4 w-4 text-green-600" aria-hidden="true" />}
                  </div>
                </li>
              )}
            </Combobox.Option>
          ))}

          {showCreateOption && (
            <Combobox.Option
              value={{ id: '__create__', name: trimmedQuery } as Provider}
              as={Fragment}
            >
              {({ active }) => (
                <li
                  className={`relative cursor-pointer select-none px-4 py-2 text-sm transition-colors border-t ${
                    active ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
                  }`}
                >
                  + Crear proveedor "{trimmedQuery}"
                </li>
              )}
            </Combobox.Option>
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  )
}
